export interface FigureTimelineItem {
  date: string;
  title: string;
  text: string;
}

export interface FigureRelation {
  name: string;
  type: string;
  note: string;
}

export interface Figure {
  id: number;
  slug: string;
  name: string;
  role: string;
  category: string;
  status: string;
  published: string;
  minutes: number;
  initial: string;
  tagline: string;
  summary: string;
  story: string[];
  timeline: FigureTimelineItem[];
  relations: FigureRelation[];
  keywords: string[];
}

export interface FigureCategory {
  key: string;
  label: string;
}

export const figureCategories: FigureCategory[] = [
  { key: "all", label: "全部档案" },
  { key: "whistle", label: "举报与揭发" },
  { key: "party", label: "涉事主角" },
  { key: "related", label: "关联当事人" },
  { key: "progress", label: "回应与进展" },
];

export const figures: Figure[] = [
  {
    id: 1,
    slug: "key-whistleblower",
    name: "关键知情人「阿舟」",
    role: "首发披露的匿名信源",
    category: "whistle",
    status: "证据核实中",
    published: "09-06",
    minutes: 8,
    initial: "舟",
    tagline: "最早递出内部材料的吹哨人，牵出整条证据链的第一环。",
    summary: "在一桩行业乱象调查进入公众视野之前，关键知情人「阿舟」是最早向编辑部递出内部文件与聊天记录的匿名信源。其提供的素材从会议纪要、财务单据到工作群截图，覆盖面广，为后续涉事主角与关联当事人的浮出提供了起点。",
    story: [
      "「阿舟」的首次现身，是通过一条加密投递渠道。其所附材料日期跨度超过两年，包含多份标注内部使用的工作文档，直接指向一项长期存在的违规操作流程。编辑部在完成初步核验、确认材料未被篡改后，才决定将其纳入报道线索库。",
      "核实过程中，「阿舟」反复强调三件事：其一，希望隐去具体职务以免牵连身边仍在职的同事；其二，愿意接受电话交叉询问以佐证材料细节；其三，暂时无法公开露面，但承诺在关键节点补充后续证据。这些要求被编辑部逐一记录，并据此建立了匿名信源的沟通协议。",
      "随着话题发酵，外界出现对信源身份的种种猜测。编辑部在更新中说明，报道依据的是可交叉验证的证据而非单一陈述，「阿舟」的角色是材料提供者与过程见证人，并非事件被指控的责任主体，请读者避免对化名身份的过度联想。",
      "目前，「阿舟」仍在配合编辑团队对新增材料做进一步鉴别，其能否提供更多独立佐证，将直接影响这条线索能否进入下一阶段的人物关联梳理。",
    ],
    timeline: [
      { date: "08-28", title: "匿名投递首批材料", text: "通过加密渠道提交两份内部文档与十余张截图，编辑部启动真伪初筛。" },
      { date: "09-01", title: "交叉访谈达成", text: "完成首次电话访谈，就材料时间线与操作细节作逐条确认，内容一致。" },
      { date: "09-04", title: "启动涉事主体识别", text: "基于其材料，初步圈定被点名的主体角色，进入关联当事人核查阶段。" },
      { date: "09-08", title: "补充证据鉴别中", text: "对第二批材料进行电子取证与来源校验，状态更新为证据核实中。" },
    ],
    relations: [
      { name: "被点名的涉事主角", type: "举报对象", note: "材料中多次出现的具体操作责任人，目前尚未公开回应。" },
      { name: "协助核实的记者", type: "对接人", note: "负责全程复核材料的编辑部成员，保障信息单向传递。" },
      { name: "同批在职信源", type: "潜在佐证", note: "尚未确认身份，可能用于交叉验证后续关键节点。" },
    ],
    keywords: ["最新爆料", "关键知情人", "匿名信源", "材料核实"],
  },
  {
    id: 2,
    slug: "named-protagonist",
    name: "被点名者「张某」",
    role: "事件中反复出现的主要涉事人",
    category: "party",
    status: "已发声明",
    published: "09-05",
    minutes: 6,
    initial: "张",
    tagline: "在爆料中被多次直接点名，其回应声明成为观察风向的关键节点。",
    summary: "被点名者「张某」是近期报道中反复出现的主要涉事人物。随着材料与访谈的推进，其在事件链条中的具体角色逐渐清晰：既是相关流程的审批端，也是部分往来记录的落款方，正因如此，其态度与后续动作受到读者高度关注。",
    story: [
      "编辑部首次提到「张某」时，仅以职务口径出现。随后经多方交叉比对，其在事件中的位置被进一步锁定——数份内部文档的审批栏与回执均指向同一人，这一信息在关联当事人之间相互印证。",
      "爆料公开后的第三个工作日，「张某」通过所在团队发布了一则简短声明，表示已注意到相关传闻，愿意配合内部与外部调查，并呼吁以事实为准。声明措辞克制，并未对具体指控逐条回应，这被不少读者视为事件仍处于进展中的信号。",
      "编辑部对声明进行了事实性转述，并在同一更新中提示：声明内容不属于证据，后续仍将以可验证材料为判断依据，避免因单一表态影响对人物角色的判断。",
      "接下来观察的重点，集中在「张某」是否会补充披露佐证材料、其职务安排是否变动，以及涉事主体的关联当事人是否随之现身。",
    ],
    timeline: [
      { date: "09-03", title: "首次以职务口径提及", text: "在多份交叉材料中确认其审批端角色，尚未点名。" },
      { date: "09-05", title: "身份信息进一步锁定", text: "经关联当事人互证，确认其为事件的主要涉事人之一。" },
      { date: "09-07", title: "发布简短回应声明", text: "表示愿配合调查、以事实为准，未逐条回应指控。" },
    ],
    relations: [
      { name: "关键知情人「阿舟」", type: "材料关联", note: "材料中出现的具体责任人，二人构成披露与回应的核心链条。" },
      { name: "涉事团队", type: "所属主体", note: "其所在团队对外发布声明，属同一事件参与方。" },
      { name: "关联当事人「陈某」", type: "合作往来", note: "多份往来记录中出现，关联度待进一步确认。" },
    ],
    keywords: ["最新爆料", "被点名者", "涉事主角", "回应声明"],
  },
  {
    id: 3,
    slug: "relational-party",
    name: "关联当事人「陈某」",
    role: "与被点名者存在业务交集的关联方",
    category: "related",
    status: "待交叉核实",
    published: "09-04",
    minutes: 5,
    initial: "陈",
    tagline: "与被点名者有过合作与往来，其角色性质仍待进一步厘清。",
    summary: "关联当事人「陈某」之所以进入视野，是因为多份往来记录中均出现其名下主体与被点名者的业务交集。目前编辑部将其定性为关联当事人，即「有交集但角色待定」，尚未将其归入被指控一方。",
    story: [
      "「陈某」最初并未出现在首发材料中，而是在梳理资金与合同流向时浮出。检索结果显示，其名下主体与被点名者之间存在多笔项目往来，时间与事件窗口高度重合，这使其从众多合作方中被单独圈出。",
      "编辑部强调，「交集」不等于「涉事」。为避免误伤，编辑团队对「陈某」仅作关联标注，并注明其角色性质尚未经过完整核实，是否升级为涉事主体将取决于后续材料。",
      "在公开的人物关系图中，「陈某」被放置在被点名者的次级节点，连线类型标注为「业务往来待核实」，这一克制处理也获得了部分读者的认可。",
    ],
    timeline: [
      { date: "09-04", title: "资金与合同流向检索", text: "发现其名下主体与被点名者存在多笔项目交集。" },
      { date: "09-06", title: "标记为关联当事人", text: "列入关系图次级节点，角色性质标注为待核实。" },
      { date: "09-09", title: "发起背景交叉问询", text: "就合作性质向相关主体发送核实问询，尚未回复。" },
    ],
    relations: [
      { name: "被点名者「张某」", type: "业务往来", note: "多笔项目交集，构成关联检索的起点。" },
      { name: "关键知情人「阿舟」", type: "材料外线索", note: "其往来出现在阿舟材料之外，属编辑部自行核实的补充线索。" },
      { name: "第三方合作主体", type: "延伸节点", note: "与其共同参与项目的其他主体，暂不展开追踪。" },
    ],
    keywords: ["最新爆料", "关联当事人", "业务交集", "角色待定"],
  },
  {
    id: 4,
    slug: "on-site-witness",
    name: "现场当事人「小蔡」",
    role: "第一时间提交现场记录的在场者",
    category: "related",
    status: "记录已归档",
    published: "09-03",
    minutes: 7,
    initial: "蔡",
    tagline: "在事件发生的现场记录下第一手片段，为还原经过提供时间线佐证。",
    summary: "现场当事人「小蔡」并不涉及事件核心决策，但其提交的现场时间线与影像记录，为编辑部还原事件经过提供了重要的过程佐证。其视角属于外围亲历者，所提供素材主要用于校准关键节点的时间。",
    story: [
      "「小蔡」与事件的交集发生在一个公开场合：其当时恰好在场，并用手机记录下若干片段。事后看到相关报道时，他主动联系编辑部，表示愿意提供当天的原始记录，帮助核对事件发生的确切顺序。",
      "编辑部核对后认为，其记录的节点与材料中的时间戳相互吻合，可作为过程性的辅助证据，但其视角无法覆盖事件全貌，因此仅将素材定位为时间线佐证，而非对人物责任的判断依据。",
      "出于对其个人安全的考量，编辑部在呈现时隐去其具体身份信息，仅保留「现场记录者」这一通用身份，并在时间线中注明素材来源。",
    ],
    timeline: [
      { date: "09-01", title: "主动提交现场记录", text: "联系编辑部，提交当天拍摄的原始片段与文字备忘。" },
      { date: "09-02", title: "时间线交叉校准", text: "其记录与材料时间戳吻合，作为过程佐证归入档案。" },
      { date: "09-03", title: "素材完成归档", text: "记录完成匿名化处理，状态更新为记录已归档。" },
    ],
    relations: [
      { name: "被点名者「张某」", type: "同场见证", note: "其记录中出现涉事场景，但不构成责任关联。" },
      { name: "编辑部", type: "素材提供方", note: "以外围亲历者身份提交一手记录。" },
      { name: "关键知情人「阿舟」", type: "时间互证", note: "两方素材共同校准了关键节点的时间顺序。" },
    ],
    keywords: ["最新爆料", "现场当事人", "亲历记录", "时间线佐证"],
  },
  {
    id: 5,
    slug: "internal-network",
    name: "内部信源「线人群组」",
    role: "多名在职信源组成的分散观察者",
    category: "whistle",
    status: "持续补充",
    published: "09-02",
    minutes: 9,
    initial: "群",
    tagline: "来自不同岗位的分散信源，用各自的片段拼出更完整的内部图景。",
    summary: "「线人群组」并非单一个人，而是编辑部在报道推进中陆续接触到的多名在职信源的合称。他们彼此可能互不相识，却因提供各自岗位视角下的片段材料，共同让事件的内部图景变得更加立体。",
    story: [
      "在首份材料公开后，编辑部陆续接到来自不同岗位的反馈，有人补充制度执行细节，有人提供实际发生的操作案例，也有人对既有材料的某些表述提出异议。为保护这些分散信源，编辑部以「内部信源」这一合称统一呈现。",
      "编辑团队并不轻信任何单一信源的描述。每一条来自线人群组的补充都会被要求提供可验证的旁证，或与其他材料交叉印证后才能进入报道，避免以孤证支撑结论。",
      "这些分散片段最终被汇总成一张内部操作图谱：它不指向某一具体个人的过错，而是呈现出一套长期存在的流程性失序。编辑部据此将关注重心从「追责个人」部分转向「厘清机制」，这也是人物关系梳理中的一个重要转折。",
    ],
    timeline: [
      { date: "08-30", title: "开始收到岗位反馈", text: "多名在职信源分别就制度执行与操作细节补充材料。" },
      { date: "09-02", title: "汇总为内部信源图谱", text: "将分散片段交叉印证后，合并为统一的内部图景呈现。" },
      { date: "09-06", title: "补充机制性观察", text: "新增对流程性失序的分析，标志追踪视角向机制延伸。" },
    ],
    relations: [
      { name: "被点名者「张某」", type: "流程相关", note: "其岗位处于该流程的审批端，属图谱中的一环。" },
      { name: "关键知情人「阿舟」", type: "互补信源", note: "与阿舟材料相互印证，共同支撑内部图景。" },
      { name: "编辑部", type: "信任关系", note: "以合称呈现，不暴露各自具体岗位，避免波及在职信源。" },
    ],
    keywords: ["最新爆料", "内部信源", "在职信源", "流程失序"],
  },
  {
    id: 6,
    slug: "response-spokesperson",
    name: "回应方代表「孙先生」",
    role: "涉事主体一方的对外回应人",
    category: "progress",
    status: "等待后续表态",
    published: "09-01",
    minutes: 4,
    initial: "孙",
    tagline: "站在被点名主体一侧回应外界关切，其表态口径的松紧是观察焦点。",
    summary: "「孙先生」作为被点名主体一方的对外回应代表，负责在报道公开后就外界关切进行说明。编辑部将其纳入档案，并非认定其个人责任，而是视其为观察「回应与进展」这条线索的重要坐标。",
    story: [
      "当首份报道公开后，「孙先生」在当天即就媒体问询作出回应，表示相关情况正在梳理中，并承诺将在核实后进一步说明。这类「初步回应」通常是后续表态的开端。",
      "编辑部注意到，回应方代表的口径在短时间内有所调整：从最初的「情况不明」逐步变为「愿配合核实」，这一变化往往意味着内部已启动处置流程，是人物追踪中值得记录的信号。",
      "需要说明的是，回应代表的表态属过程性信息，编辑部在转述时均注明「未经独立核实」，并提示读者将其作为事件进展的动态参考，而非定论。",
    ],
    timeline: [
      { date: "08-31", title: "报道公开当日首度回应", text: "表示情况正在梳理中，承诺核实后进一步说明。" },
      { date: "09-01", title: "口径调整为愿配合", text: "确认已启动内部核实流程，表态相对此前更明确。" },
      { date: "09-07", title: "进入等待后续阶段", text: "暂未就具体事项表态，状态更新为等待后续回应。" },
    ],
    relations: [
      { name: "被点名者「张某」", type: "同一主体", note: "与被点名者同属一方，负责对外传达口径。" },
      { name: "编辑部", type: "问询对象", note: "面向媒体与公众承担回应职责。" },
      { name: "关联当事人「陈某」", type: "同侧协调", note: "同侧信息的协调沟通存在一定交集。" },
    ],
    keywords: ["最新爆料", "回应代表", "涉事主体", "表态动态"],
  },
];